import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { avisarImpresion } from '@/lib/imprimir';
import { UtensilsCrossed, ChefHat, ClipboardList, Bike, Plus, X, AlertTriangle, Clock, Users } from 'lucide-react';

// S128 — Restaurante: comandas, cocina, menú y reparto.
//
// La pantalla está armada alrededor de tres cosas que en un servicio lleno se
// deciden en segundos:
//
//   1. Qué mesa lleva más tiempo esperando. Por eso la cocina ordena por
//      antigüedad y marca en rojo a los 20 minutos, no por orden de captura.
//   2. Qué se puede cancelar sin costo y qué ya se cocinó. La pantalla lo dice
//      ANTES de cancelar, no después.
//   3. Qué se acabó. El «86» está a un clic desde el menú.

const PESTANAS = [
  { k: 'servicio', t: 'Servicio', icono: ClipboardList },
  { k: 'cocina', t: 'Cocina', icono: ChefHat },
  { k: 'menu', t: 'Menú', icono: UtensilsCrossed },
];

const TIPOS = {
  mesa: 'En mesa', para_llevar: 'Para llevar', reparto: 'A domicilio',
};

function Dinero({ v }) {
  return <span className="tabular-nums">${v ?? '0.00'}</span>;
}

export default function RestaurantService() {
  const [cargando, setCargando] = useState(true);
  const [pestana, setPestana] = useState('servicio');
  const [servicio, setServicio] = useState(null);
  const [cocina, setCocina] = useState(null);
  const [menu, setMenu] = useState(null);
  const [merma, setMerma] = useState(null);
  const [abierta, setAbierta] = useState(null);   // comanda abierta en detalle
  const [receta, setReceta] = useState(null);
  const [insumos, setInsumos] = useState([]);
  const [nuevoInsumo, setNuevoInsumo] = useState({ ingredient_item_id: '', quantity: 1 });
  const [nueva, setNueva] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [s, c, m, w] = await Promise.all([
        api.get('/tenant/restaurante/servicio'),
        api.get('/tenant/restaurante/cocina'),
        api.get('/tenant/restaurante/menu'),
        api.get('/tenant/restaurante/merma'),
      ]);
      setServicio(s.data); setCocina(c.data);
      setMenu(m.data.carta || []); setMerma(w.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  const [cuentas, setCuentas] = useState(null);

  const cargarCuentas = useCallback(async (id) => {
    if (!id) { setCuentas(null); return; }
    try {
      const { data } = await api.get(`/tenant/restaurante/comandas/${id}/cuentas`);
      setCuentas(data);
    } catch (e) { setCuentas({ error: formatApiError(e) }); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function abrirComanda(id) {
    try {
      const { data } = await api.get(`/tenant/restaurante/comandas/${id}`);
      setAbierta(data);
      await cargarCuentas(id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function crearComanda() {
    try {
      const cuerpo = { order_type: nueva.tipo };
      if (nueva.tipo === 'mesa') {
        cuerpo.table_resource_id = nueva.mesa;
        cuerpo.party_size = Number(nueva.personas) || null;
      } else {
        cuerpo.customer_name = nueva.cliente || null;
        cuerpo.customer_phone = nueva.telefono || null;
        if (nueva.tipo === 'reparto') {
          cuerpo.delivery_address = nueva.direccion || null;
          cuerpo.delivery_fee = Number(nueva.envio) || null;
        }
      }
      const { data } = await api.post('/tenant/restaurante/comandas', cuerpo);
      toast.success(`Cuenta ${data.folio} abierta.`);
      setNueva(null);
      await cargar();
      await abrirComanda(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function agregarPlatillo(catalogItemId, cantidad, notas) {
    try {
      await api.post(`/tenant/restaurante/comandas/${abierta.id}/renglones`, {
        catalog_item_id: catalogItemId, quantity: cantidad || 1,
        notes: notas || null,
      });
      await abrirComanda(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function mandarACocina() {
    try {
      const { data } = await api.post(
        `/tenant/restaurante/comandas/${abierta.id}/mandar-a-cocina`, {});
      toast.success(`${data.platillos} platillo(s) a cocina. ${data.aviso}`);
      // S135 — el papel para el cocinero. La comanda YA quedó guardada arriba:
      // esto solo avisa si salió o no, y si no, abre el respaldo para
      // imprimirlo a mano. Nunca deshace nada.
      avisarImpresion(data.impresion, { queEs: 'La comanda' });
      await abrirComanda(abierta.id);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  // S136 — la receta de un platillo: qué insumos gasta y cuánto.
  async function abrirReceta(p) {
    try {
      const [rec, cat] = await Promise.all([
        api.get(`/tenant/restaurante/menu/${p.id}/receta`),
        api.get('/tenant/catalog/items?item_type=product').catch(() => ({ data: { items: [] } })),
      ]);
      setReceta({ platillo: p, ...rec.data });
      setInsumos(cat.data?.items || cat.data || []);
      setNuevoInsumo({ ingredient_item_id: '', quantity: 1 });
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function guardarInsumo() {
    if (!nuevoInsumo.ingredient_item_id) { toast.error('Elige el insumo'); return; }
    try {
      await api.post(`/tenant/restaurante/menu/${receta.platillo.id}/receta`, {
        ingredient_item_id: nuevoInsumo.ingredient_item_id,
        quantity: Number(nuevoInsumo.quantity) || 1,
      });
      await abrirReceta(receta.platillo);
      toast.success('Insumo guardado.');
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function quitarInsumo(r) {
    try {
      await api.delete(`/tenant/restaurante/receta/${r.id}`);
      await abrirReceta(receta.platillo);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function reimprimirComanda() {
    try {
      const { data } = await api.get(`/tenant/restaurante/comandas/${abierta.id}/comanda-impresa`);
      avisarImpresion(data, { queEs: 'La comanda' });
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function avanzar(itemId, estado) {
    try {
      await api.post(`/tenant/restaurante/renglones/${itemId}/avanzar`, { estado });
      if (abierta) await abrirComanda(abierta.id);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function cancelarRenglon(r) {
    const aviso = r.mandado_a_cocina
      ? 'Este platillo YA está en cocina: al cancelarlo se cuenta como comida perdida.\n\n'
      : 'Este platillo todavía no llega a cocina, así que no hay desperdicio.\n\n';
    const motivo = window.prompt(aviso + '¿Por qué se cancela?');
    if (!motivo || motivo.trim().length < 3) {
      if (motivo !== null) toast.error('Escribe el motivo.');
      return;
    }
    try {
      const { data } = await api.post(
        `/tenant/restaurante/renglones/${r.id}/cancelar`, { motivo });
      toast.success(data.aviso);
      await abrirComanda(abierta.id);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function dividirIgual() {
    const cuantas = window.prompt('¿Entre cuántas cuentas se parte?\n\n'
      + 'Se reparte el consumo en partes iguales. La propina la pone cada '
      + 'quien después, porque no todos dejan lo mismo.');
    if (!cuantas) return;
    try {
      const { data } = await api.post(
        `/tenant/restaurante/comandas/${abierta.id}/dividir/partes-iguales`,
        { partes: parseInt(cuantas, 10) });
      toast.success(data.aviso);
      await cargarCuentas(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function unirCuentas() {
    if (!window.confirm('Vuelve a ser una sola cuenta. ¿Seguimos?')) return;
    try {
      const { data } = await api.post(
        `/tenant/restaurante/comandas/${abierta.id}/dividir/unir`);
      toast.success(data.aviso);
      await cargarCuentas(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function ponerPropina(cuenta) {
    const pct = window.prompt(
      `Propina de «${cuenta.etiqueta}» — consumo $${cuenta.consumo}\n\n`
      + 'Escribe el PORCENTAJE (10, 15, 20…). Se calcula sobre el consumo, '
      + 'no sobre un total que ya traiga propina.', cuenta.porcentaje_propina || '15');
    if (pct === null) return;
    try {
      const { data } = await api.post(
        `/tenant/restaurante/cuentas/${cuenta.id}/propina`, { porcentaje: pct });
      toast.success(data.aviso);
      await cargarCuentas(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function cobrarCuenta(cuenta) {
    if (!window.confirm(`Vas a marcar como pagada «${cuenta.etiqueta}» por `
        + `$${cuenta.total} (consumo $${cuenta.consumo} + propina $${cuenta.propina}).`
        + '\n\n¿Ya te pagó?')) return;
    try {
      const { data } = await api.post(
        `/tenant/restaurante/cuentas/${cuenta.id}/cobrar`, {});
      toast.success(data.aviso);
      await cargarCuentas(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function anularCobroCuenta(cuenta) {
    const motivo = window.prompt(
      `Vas a deshacer el cobro de «${cuenta.etiqueta}».\n\n`
      + '¿Por qué? Queda como constancia de que el dinero se devolvió.');
    if (!motivo) return;
    try {
      const { data } = await api.post(
        `/tenant/restaurante/cuentas/${cuenta.id}/anular-cobro`, { motivo });
      toast.success(data.aviso);
      await cargarCuentas(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function cerrar() {
    try {
      const { data } = await api.post(
        `/tenant/restaurante/comandas/${abierta.id}/cerrar`, {});
      toast.success(`Cuenta cerrada por $${data.total}. ${data.aviso}`);
      setAbierta(null);
      setCuentas(null);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function despachar() {
    const repartidor = window.prompt('¿Quién lo lleva?');
    if (repartidor === null) return;
    try {
      await api.post(`/tenant/restaurante/comandas/${abierta.id}/despachar`,
                     { courier_name: repartidor || null });
      toast.success('El pedido salió del restaurante.');
      await abrirComanda(abierta.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function entregado() {
    try {
      await api.post(`/tenant/restaurante/comandas/${abierta.id}/entregado`);
      toast.success('Pedido entregado.');
      await abrirComanda(abierta.id);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function cambiarDisponibilidad(platillo) {
    const agotar = platillo.disponible;
    let motivo = null;
    if (agotar) {
      motivo = window.prompt(`¿Por qué se acabó «${platillo.nombre}»? (lo verá el equipo)`);
      if (motivo === null) return;
    }
    try {
      const { data } = await api.post(
        `/tenant/restaurante/menu/${platillo.id}/disponibilidad`,
        { agotado: agotar, motivo });
      toast.success(data.aviso);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (cargando) return <div className="p-6 text-slate-500">Cargando el servicio…</div>;

  // ───────────────────────────────── comanda abierta
  if (abierta) {
    const platillos = (menu || []).flatMap((g) => g.platillos);
    return (
      <div className="p-6 space-y-4 max-w-5xl">
        <Button variant="ghost" onClick={() => setAbierta(null)}>← Volver al servicio</Button>

        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                {abierta.mesa || abierta.cliente || 'Cuenta'} · {abierta.folio}
              </h2>
              <div className="text-sm text-slate-500">
                {TIPOS[abierta.tipo] || abierta.tipo}
                {abierta.personas ? ` · ${abierta.personas} personas` : ''}
                {abierta.mesero ? ` · atiende ${abierta.mesero}` : ''}
              </div>
              {abierta.direccion && (
                <div className="text-sm text-slate-600 mt-1">{abierta.direccion}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold"><Dinero v={abierta.total} /></div>
              {abierta.costo_de_envio && (
                <div className="text-xs text-slate-500">
                  incluye envío <Dinero v={abierta.costo_de_envio} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border rounded-lg bg-white divide-y">
          {abierta.renglones.length === 0 && (
            <div className="p-4 text-sm text-slate-500">
              Todavía no hay nada en esta cuenta. Agrega del menú de abajo.
            </div>
          )}
          {abierta.renglones.map((r) => (
            <div key={r.id} className={`p-3 ${r.cancelado ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="font-medium">{r.cantidad}× {r.platillo}</span>
                  {r.opciones.map((o) => (
                    <span key={o.nombre} className="ml-2 text-xs px-1.5 py-0.5 rounded bg-slate-100">
                      {o.nombre}
                    </span>
                  ))}
                  {r.notas && <div className="text-xs text-amber-700">Nota: {r.notas}</div>}
                  {r.cancelado && (
                    <div className="text-xs text-rose-700">
                      Cancelado — {r.motivo_cancelacion}
                      {r.se_perdio_la_comida && ' · se perdió la comida'}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div><Dinero v={r.importe} /></div>
                  <div className="text-xs text-slate-500">{r.etiqueta}</div>
                </div>
              </div>
              {!r.cancelado && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {r.siguientes.map((s) => (
                    <Button key={s.estado} size="sm" variant="outline"
                            onClick={() => avanzar(r.id, s.estado)}>
                      {s.etiqueta}
                    </Button>
                  ))}
                  <Button size="sm" variant="ghost" className="text-rose-600"
                          onClick={() => cancelarRenglon(r)}>
                    Cancelar
                  </Button>
                  {!r.mandado_a_cocina && (
                    <span className="text-xs text-slate-500 self-center">
                      todavía sin mandar a cocina
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {abierta.sin_mandar_a_cocina > 0 && (
            <Button onClick={mandarACocina}>
              <ChefHat className="w-4 h-4 mr-1" />
              Mandar {abierta.sin_mandar_a_cocina} a cocina
            </Button>
          )}
          {abierta.tipo === 'reparto' && !abierta.salio && (
            <Button variant="outline" onClick={despachar}>
              <Bike className="w-4 h-4 mr-1" /> Sacar a reparto
            </Button>
          )}
          {abierta.tipo === 'reparto' && abierta.salio && !abierta.entregado && (
            <Button variant="outline" onClick={entregado}>Marcar entregado</Button>
          )}
          <Button variant="secondary" onClick={cerrar}>Cerrar cuenta y cobrar</Button>
        </div>

        {/* ── Cuenta dividida y propina ── */}
        <div className="border rounded-lg p-4 bg-white space-y-3" data-testid="cuenta-dividida">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" /> Cómo se paga esta mesa
              <HelpTooltip text={'Partir la cuenta entre PERSONAS. No lo confundas con los '
                + 'pagos divididos de la caja, que parten UN cobro entre varios métodos '
                + '(mitad tarjeta, mitad efectivo). Se combinan: cada persona de aquí puede '
                + 'pagar lo suyo como quiera.'} />
            </h3>
            <div className="flex gap-2">
              {cuentas?.modo === 'sin_dividir' ? (
                <Button size="sm" variant="outline" onClick={dividirIgual}>
                  Dividir en partes iguales
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={unirCuentas}>
                  Volver a una sola cuenta
                </Button>
              )}
            </div>
          </div>

          {cuentas?.error && (
            <p className="text-sm text-rose-700">No se pudo consultar: {cuentas.error}</p>
          )}

          {cuentas && !cuentas.error && cuentas.modo === 'sin_dividir' && (
            <p className="text-sm text-slate-600">
              Paga una sola persona. Si en la mesa piden cuentas separadas, divide
              antes de cobrar — después de que alguien pague ya no se puede repartir.
            </p>
          )}

          {cuentas?.descuadre && (
            <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded text-sm">
              <b>Ojo: se agregó algo después de repartir.</b> Faltan
              {' '}<Dinero v={cuentas.descuadre} /> por asignar. Vuelve a dividir
              para que la suma de las cuentas cuadre con lo que se consumió.
            </div>
          )}

          {(cuentas?.subcuentas || []).length > 0 && (
            <div className="space-y-2">
              {cuentas.subcuentas.map((c) => (
                <div key={c.id}
                     className={`flex items-center justify-between gap-3 border rounded p-2 flex-wrap
                                 ${c.estado === 'cobrada' ? 'bg-emerald-50/60 border-emerald-200' : ''}`}>
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      {c.etiqueta}
                      {c.estado === 'cobrada' && (
                        <span className="text-xs text-emerald-700">pagada</span>
                      )}
                      {c.lleva_el_centavo && (
                        <span className="text-xs text-slate-500">
                          (lleva el centavo del redondeo)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600">
                      Consumo <Dinero v={c.consumo} /> + propina <Dinero v={c.propina} />
                      {c.porcentaje_propina && ` (${c.porcentaje_propina}%)`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-semibold tabular-nums">
                      <Dinero v={c.total} />
                    </div>
                    {c.estado === 'cobrada' ? (
                      <Button size="sm" variant="ghost"
                              onClick={() => anularCobroCuenta(c)}>Deshacer</Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline"
                                onClick={() => ponerPropina(c)}>Propina</Button>
                        <Button size="sm" onClick={() => cobrarCuenta(c)}>Cobrar</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-between text-sm pt-1 border-t">
                <span className="text-slate-600">
                  Cobrado <Dinero v={cuentas.cobrado} /> de
                  {' '}<Dinero v={cuentas.total_a_cobrar} />
                </span>
                <span className="font-medium">
                  Falta <Dinero v={cuentas.falta_por_cobrar} />
                </span>
              </div>
            </div>
          )}

          {/* Este límite se dice a la vista: es la pregunta que hace todo mesero
              la primera vez, y no puede depender de que abra un tooltip. */}
          <p className="text-xs text-slate-500">
            La propina va aparte del consumo: no lleva IVA y es del mesero, no del
            restaurante. Cerrar la mesa se hace al final, cuando ya pagaron todos.
          </p>
        </div>

        <div>
          <h3 className="font-semibold mb-2">Agregar del menú</h3>
          <div className="space-y-3">
            {(menu || []).map((g) => (
              <div key={g.tiempo}>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  {g.tiempo}
                </div>
                <div className="flex flex-wrap gap-2">
                  {g.platillos.map((p) => (
                    <Button key={p.id} size="sm" variant="outline"
                            disabled={!p.disponible}
                            title={p.disponible ? '' : `Agotado: ${p.motivo_agotado || ''}`}
                            onClick={() => agregarPlatillo(p.id, 1)}>
                      <Plus className="w-3 h-3 mr-1" />
                      {p.nombre} {p.precio ? `· $${p.precio}` : ''}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
            {platillos.length === 0 && (
              <div className="text-sm text-slate-500">
                Tu menú está vacío. Ve a la pestaña <b>Menú</b> para cargarlo.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────── tablero
  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <div className="flex items-center gap-2">
        <UtensilsCrossed className="w-6 h-6 text-orange-600" />
        <h1 className="text-2xl font-semibold">Restaurante</h1>
      </div>

      <SectionHelp
        module="restaurante"
        title="la operación del restaurante"
        description={'Aquí se abren las cuentas, se manda a cocina, se cobra y se sacan '
                     + 'los pedidos a domicilio. Las reservas y el mapa de mesas viven '
                     + 'en Agenda.'}
        whatItDoesNot={'no borra platillos ya pedidos. Se cancelan con motivo, para que '
                       + 'la comida que se cocinó y no se cobró aparezca en el reporte '
                       + 'de merma en vez de desaparecer.'} />

      {/* Estas dos reglas cambian cómo se usa la pantalla, así que van siempre
          visibles y no escondidas en la ayuda plegable. */}
      <div className="border-l-4 border-orange-400 bg-orange-50/50 pl-3 py-2 text-sm text-slate-700 space-y-1">
        <div>
          <b>El precio se congela al pedir.</b> Si cambias un precio del menú, las
          cuentas que ya están abiertas siguen con el precio que el cliente aceptó.
        </div>
        <div>
          <b>Cancelar algo que ya está en cocina cuenta como comida perdida.</b> La
          pantalla te avisa antes de hacerlo, y el motivo queda registrado.
        </div>
      </div>

      <div className="flex gap-2 border-b">
        {PESTANAS.map(({ k, t, icono: Icono }) => (
          <button key={k} onClick={() => setPestana(k)}
                  className={`px-3 py-2 text-sm flex items-center gap-1 border-b-2 ${
                    pestana === k ? 'border-orange-500 font-medium' : 'border-transparent text-slate-500'}`}>
            <Icono className="w-4 h-4" /> {t}
          </button>
        ))}
      </div>

      {pestana === 'servicio' && (
        <>
          {!servicio?.abiertas && !servicio?.cuentas_cerradas_hoy ? (
            <div className="border rounded-lg p-6 bg-slate-50 text-slate-700 space-y-2">
              <div className="font-medium">Todavía no hay ninguna cuenta abierta.</div>
              <p className="text-sm">
                Abre una cuenta cuando llegue una mesa, o cuando entre un pedido para
                llevar o a domicilio. Si el menú está vacío, cárgalo primero desde la
                pestaña <b>Menú</b>.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              <Tarjeta n={servicio.mesas_ocupadas} t="Mesas ocupadas" />
              <Tarjeta n={servicio.repartos_en_curso} t="Repartos en curso" />
              <Tarjeta n={servicio.cuentas_cerradas_hoy} t="Cuentas de hoy" />
              <Tarjeta n={`$${servicio.venta_de_hoy}`} t="Venta de hoy" resaltar />
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={() => setNueva({ tipo: 'mesa' })}>
              <Plus className="w-4 h-4 mr-1" /> Abrir cuenta
            </Button>
            {merma?.platillos > 0 && (
              <span className="self-center text-sm text-rose-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Merma de hoy: {merma.platillos} platillo(s) · ${merma.importe}
                <HelpTooltip text="Comida que se cocinó y no se cobró porque se canceló después de mandarla a cocina." />
              </span>
            )}
          </div>

          <div className="space-y-2">
            {(servicio?.comandas || []).map((c) => (
              <button key={c.id} onClick={() => abrirComanda(c.id)}
                      className="w-full text-left border rounded-lg p-3 bg-white hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {c.mesa || c.cliente || 'Cuenta'} · {c.folio}
                  </span>
                  <span className="font-medium"><Dinero v={c.total} /></span>
                </div>
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <span>{TIPOS[c.tipo] || c.tipo}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {c.minutos_abierta} min
                  </span>
                  {c.platillos_pendientes > 0 && (
                    <span className="text-amber-700">
                      {c.platillos_pendientes} sin entregar
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {pestana === 'cocina' && (
        <div className="space-y-4">
          {cocina?.total_pendiente === 0 ? (
            <div className="border rounded-lg p-6 bg-slate-50 text-slate-700">
              <div className="font-medium">La cocina está al día.</div>
              <p className="text-sm">Aquí aparece lo que falta por preparar en cuanto
                un mesero mande una cuenta a cocina.</p>
            </div>
          ) : (
            <div className="text-sm">
              {cocina.total_pendiente} platillo(s) pendientes
              {cocina.demorados > 0 && (
                <span className="ml-2 text-rose-700 font-medium">
                  · {cocina.demorados} con más de 20 minutos
                </span>
              )}
            </div>
          )}
          {(cocina?.estaciones || []).map((e) => (
            <div key={e.estacion}>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                {e.estacion}
              </div>
              <div className="space-y-2">
                {e.platillos.map((p) => (
                  <div key={p.id}
                       className={`border rounded-lg p-3 ${p.demorado ? 'border-rose-300 bg-rose-50' : 'bg-white'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{p.cantidad}× {p.platillo}</span>
                      <span className={`text-sm ${p.demorado ? 'text-rose-700 font-medium' : 'text-slate-500'}`}>
                        {p.minutos_esperando} min
                      </span>
                    </div>
                    <div className="text-sm text-slate-500">
                      {p.mesa || TIPOS[p.tipo]} · {p.folio} · {p.etiqueta}
                    </div>
                    {p.notas && <div className="text-sm text-amber-700">Nota: {p.notas}</div>}
                    <div className="flex gap-2 mt-2">
                      {p.siguientes.map((s) => (
                        <Button key={s.estado} size="sm" variant="outline"
                                onClick={() => avanzar(p.id, s.estado)}>
                          {s.etiqueta}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {receta && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setReceta(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto p-4 space-y-3"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold">Receta de {receta.platillo.nombre}</h3>
                <p className="text-xs text-slate-500 mt-1">{receta.ayuda}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setReceta(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-1">
              {receta.insumos.length === 0 && (
                <p className="text-sm text-slate-500">
                  Todavía no tiene insumos. Mientras esté así, vender este
                  platillo no mueve el almacén.
                </p>
              )}
              {receta.insumos.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 border rounded p-2 text-sm">
                  <div className="min-w-0">
                    <p className="truncate">{r.nombre}</p>
                    <p className="text-xs text-slate-500">
                      {r.cantidad} por porción
                      {r.lleva_inventario
                        ? ` · quedan ${r.disponible}`
                        : ' · sin control de inventario, no se descuenta'}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => quitarInsumo(r)}>Quitar</Button>
                </div>
              ))}
            </div>

            <div className="border rounded p-3 space-y-2">
              <p className="text-sm font-medium">Agregar un insumo</p>
              <div className="grid gap-2 sm:grid-cols-[1fr_90px_auto]">
                <select className="border rounded px-2 py-1.5 text-sm"
                  value={nuevoInsumo.ingredient_item_id}
                  onChange={(e) => setNuevoInsumo({ ...nuevoInsumo, ingredient_item_id: e.target.value })}>
                  <option value="">Elige el insumo…</option>
                  {insumos.filter((x) => x.id !== receta.platillo.id).map((x) => (
                    <option key={x.id} value={x.id}>{x.name}</option>
                  ))}
                </select>
                <Input type="number" min={0.001} step="0.1" value={nuevoInsumo.quantity}
                  onChange={(e) => setNuevoInsumo({ ...nuevoInsumo, quantity: e.target.value })} />
                <Button onClick={guardarInsumo}>Guardar</Button>
              </div>
              <p className="text-[11px] text-slate-500">
                La cantidad es lo que se gasta al vender <b>una</b> porción. Si
                vendes tres, se descuenta tres veces.
              </p>
            </div>
          </div>
        </div>
      )}

      {pestana === 'menu' && (
        <div className="space-y-4">
          {(menu || []).length === 0 && (
            <div className="border rounded-lg p-6 bg-slate-50 text-slate-700 space-y-2">
              <div className="font-medium">Tu menú está vacío.</div>
              <p className="text-sm">
                Los platillos se cargan desde <b>Catálogo</b>, eligiendo el tipo
                «platillo». Ahí les pones precio, tiempo (entrada, fuerte, postre)
                y en qué estación de la cocina se preparan.
              </p>
            </div>
          )}
          {(menu || []).map((g) => (
            <div key={g.tiempo}>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                {g.tiempo}
              </div>
              <div className="border rounded-lg bg-white divide-y">
                {g.platillos.map((p) => (
                  <div key={p.id} className="p-3 flex items-center justify-between gap-3">
                    <HelpTooltip text="Qué insumos gasta este platillo. Al mandarlo a cocina, esto se descuenta del almacén automáticamente.">
                      <Button variant="ghost" size="sm" onClick={() => abrirReceta(p)}>Receta</Button>
                    </HelpTooltip>
                    <div>
                      <span className={`font-medium ${p.disponible ? '' : 'line-through text-slate-400'}`}>
                        {p.nombre}
                      </span>
                      {p.precio && <span className="ml-2 text-slate-600">${p.precio}</span>}
                      {p.estacion && (
                        <span className="ml-2 text-xs text-slate-500">{p.estacion}</span>
                      )}
                      {!p.disponible && p.motivo_agotado && (
                        <div className="text-xs text-rose-700">Agotado: {p.motivo_agotado}</div>
                      )}
                      {p.alergenos?.length > 0 && (
                        <div className="text-xs text-amber-700">
                          Alérgenos: {p.alergenos.join(', ')}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant={p.disponible ? 'outline' : 'secondary'}
                            onClick={() => cambiarDisponibilidad(p)}>
                      {p.disponible ? 'Se acabó' : 'Volver a activar'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {nueva && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-6 overflow-y-auto z-50">
          <div className="bg-white rounded-lg p-5 w-full max-w-lg space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Abrir cuenta</h3>
              <Button variant="ghost" size="sm" onClick={() => setNueva(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              {Object.entries(TIPOS).map(([k, t]) => (
                <Button key={k} size="sm"
                        variant={nueva.tipo === k ? 'default' : 'outline'}
                        onClick={() => setNueva({ ...nueva, tipo: k })}>{t}</Button>
              ))}
            </div>
            {nueva.tipo === 'mesa' ? (
              <>
                <Input placeholder="Identificador de la mesa"
                       onChange={(e) => setNueva({ ...nueva, mesa: e.target.value })} />
                <p className="text-xs text-slate-500">
                  Las mesas se dan de alta en <b>Agenda → Recursos</b>; aquí se usa la
                  misma mesa que ya tienes en el mapa del salón.
                </p>
                <Input placeholder="¿Cuántas personas?"
                       onChange={(e) => setNueva({ ...nueva, personas: e.target.value })} />
              </>
            ) : (
              <>
                <Input placeholder="Nombre del cliente"
                       onChange={(e) => setNueva({ ...nueva, cliente: e.target.value })} />
                <Input placeholder="Teléfono"
                       onChange={(e) => setNueva({ ...nueva, telefono: e.target.value })} />
                {nueva.tipo === 'reparto' && (
                  <>
                    <Textarea placeholder="Dirección completa con número y referencias"
                              onChange={(e) => setNueva({ ...nueva, direccion: e.target.value })} />
                    <Input placeholder="Costo de envío"
                           onChange={(e) => setNueva({ ...nueva, envio: e.target.value })} />
                  </>
                )}
              </>
            )}
            <Button onClick={crearComanda}>Abrir</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Tarjeta({ n, t, resaltar }) {
  return (
    <div className={`border rounded-lg p-3 ${resaltar ? 'bg-orange-50 border-orange-200' : 'bg-white'}`}>
      <div className="text-2xl font-semibold tabular-nums">{n ?? 0}</div>
      <div className="text-sm text-slate-500">{t}</div>
    </div>
  );
}
